import { useEffect, useRef } from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { Loader } from 'components/Loader';

import { useAppState } from '../../AppStateProvider';
import { appStore } from '../../services/appStore';

import { ToastView, ToastContent, ToastMainContent, StyledCloseButton, StyledToastLink } from './styles';
import { ToastIcon } from './ToastIcon';

const DEFAULT_TIMEOUT = 4000;

export const ToastProvider = () => {
  const { toast } = useAppState();
  const toastElRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const closeToast = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    appStore.setToast(undefined);
  };

  useEffect(() => {
    if (!toast) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!toast.isLoading) {
      timeoutRef.current = setTimeout(() => {
        appStore.setToast(undefined);
        timeoutRef.current = null;
      }, toast.timeout ?? DEFAULT_TIMEOUT);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [toast]);

  if (!toast) {
    return null;
  }

  return (
    <ToastView ref={toastElRef}>
      <ToastMainContent>
        <ToastIcon toast={toast} />
        <ToastContent>{toast.content}</ToastContent>
      </ToastMainContent>
      {toast.linkText && toast.link ? (
        <StyledToastLink href={toast.link} onClick={closeToast} target="_blank" rel="noopener noreferrer">
          {toast.linkText}
        </StyledToastLink>
      ) : null}
      {toast.isLoading ? <Loader /> : null}
      {toast.showCloseIcon ? (
        <StyledCloseButton onClick={closeToast} aria-label="Close toast">
          <SWReactIcons iconName="close" size="10px" iconColor={colorsPalettes.carbon[300]} />
        </StyledCloseButton>
      ) : null}
    </ToastView>
  );
};
